/**
 * A guest's round that has been entered but not yet confirmed received by the relay.
 *
 * On a poor connection a guest used to tap "Record Round" and see nothing — the request
 * failed quietly, the round was never sent, and the host waited for something that was
 * never coming. Now the round is written to this phone FIRST, then sent; if the send
 * can't get through it stays here and is retried automatically (see `app.ts`), and the
 * guest is told plainly that the host has not received it yet.
 *
 * Retrying is safe: every attempt carries the same `clientSubmissionId`, and the relay
 * treats a repeat of an id it already has as a no-op (`relay/gameShare.ts`'s
 * `submitRound`) — so a round whose acknowledgement was lost in transit is never
 * recorded twice.
 *
 * One round at a time: while one is waiting, the form is held (a second round could
 * otherwise overtake the first). Kept per game in localStorage, so it survives the guest
 * closing the tab or the phone restarting.
 */

import type { StorageLike } from './recognitionAccuracy';

export interface OutboxItem {
  gameId: string;
  kind: 'win' | 'draw';
  payload: unknown;
  clientSubmissionId: string;
  queuedAt: number;
  /** Sends that did not get through. */
  attempts: number;
  /** When the last send was tried. */
  lastTriedAt?: number;
  /** The photo was taken out of the round because sending it timed out; it is sent separately (see `photoDelivery.ts`). */
  photosHeldBack?: boolean;
}

const KEY_PREFIX = 'mahjong-scorer:guestOutbox:';

/** Used only when localStorage refuses the write (full or blocked) — lasts until the page is closed. */
const memory = new Map<string, OutboxItem>();

function defaultStorage(): StorageLike {
  return localStorage;
}

export type SaveResult = 'saved' | 'saved-without-photos' | 'memory-only';

/**
 * Saves the round on this device. Photos are reference material, not needed to score the round,
 * and can be large: if the whole thing won't fit they are dropped rather than lose the round.
 */
export function saveOutbox(item: OutboxItem, storage: StorageLike = defaultStorage()): SaveResult {
  memory.set(item.gameId, item);
  const key = KEY_PREFIX + item.gameId;
  try {
    storage.setItem(key, JSON.stringify(item));
    memory.delete(item.gameId);
    return 'saved';
  } catch {
    // fall through
  }
  const slim = withoutPhotos(item);
  try {
    storage.setItem(key, JSON.stringify(slim));
    memory.delete(item.gameId);
    return 'saved-without-photos';
  } catch {
    memory.set(item.gameId, slim);
    return 'memory-only';
  }
}

function withoutPhotos(item: OutboxItem): OutboxItem {
  if (typeof item.payload !== 'object' || item.payload === null) return item;
  const { photos: _photos, ...rest } = item.payload as Record<string, unknown>;
  return { ...item, payload: rest };
}

function isItem(value: unknown, gameId: string): value is OutboxItem {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v['gameId'] === gameId &&
    (v['kind'] === 'win' || v['kind'] === 'draw') &&
    typeof v['clientSubmissionId'] === 'string' &&
    typeof v['queuedAt'] === 'number' &&
    typeof v['attempts'] === 'number' &&
    'payload' in v
  );
}

export function loadOutbox(gameId: string, storage: StorageLike = defaultStorage()): OutboxItem | undefined {
  try {
    const raw = storage.getItem(KEY_PREFIX + gameId);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isItem(parsed, gameId)) return parsed;
    }
  } catch {
    // unreadable: fall back to memory
  }
  return memory.get(gameId);
}

/** Records that a send was tried and did not get through. */
export function noteFailedAttempt(gameId: string, now: number = Date.now(), storage: StorageLike = defaultStorage()): OutboxItem | undefined {
  const item = loadOutbox(gameId, storage);
  if (!item) return undefined;
  const next: OutboxItem = { ...item, attempts: item.attempts + 1, lastTriedAt: now };
  saveOutbox(next, storage);
  return next;
}

export function clearOutbox(gameId: string, storage: StorageLike = defaultStorage()): void {
  memory.delete(gameId);
  try {
    storage.removeItem(KEY_PREFIX + gameId);
  } catch {
    // nothing more to do
  }
}

/** The line shown to the guest while a round is waiting. */
export function describeOutbox(item: OutboxItem, sending: boolean): string {
  if (sending) return 'Sending your round…';
  const when = item.lastTriedAt
    ? ` Last tried ${new Date(item.lastTriedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`
    : '';
  const photo = item.photosHeldBack
    ? ' It is being sent without its photo because the connection is slow; the photo will follow and be added to the PDF once the connection improves.'
    : '';
  return `Couldn't reach the server yet. Your round is saved on this phone and will be sent automatically — the main device has NOT received it yet.${photo}${when}`;
}
